import { css } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { translationUpdate } from 'redux/actions/users';
import { useSelector } from 'react-redux';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { getCurrentUser } from 'redux/selectors/users';
import { useAppDispatch } from 'redux/store';
import { gray5, gray7, white } from 'styles/global_defaults/colors';
import { headerLineHeight } from 'styles/global_defaults/fonts';
import { doubleSpacing, halfSpacing, largeSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import t from 'react-translate';
import { QuizQuestionType } from 'redux/schemas/models/quiz';
import EditableOption from './editable-quiz-option';
import { QuizRichTextElement, QuizSimpleTextComponent } from './helper';

type EditableQuizBaseProps = {
  componentType: ComponentType,
  currentQuiz,
  currentQuestions,
  containerClass,
  onSaveRef,
  setSavingDisabled,
  lectureComponent,
  editInfo,
};

enum QuestionParameters {
  QUESTION_TEXT = 'questionText',
  HORIZONTAL_AXIS = 'horizontalAxisLabel',
  VERTICAL_AXIS = 'verticalAxisLabel',
}

enum OptionParameters {
  OPTION_CONTENT = 'optionContent',
  EXPLANATION = 'explanation',
  SECONDARY_EXPLANATION = 'secondaryExplanation',
}

const EditableQuizBase = ({
  componentType,
  currentQuiz,
  currentQuestions,
  containerClass,
  onSaveRef,
  setSavingDisabled,
  lectureComponent,
  editInfo,
}: EditableQuizBaseProps) => {
  const dispatch = useAppDispatch();
  const initialState = {
    activity: {
      title: currentQuiz.title,
    },
    questions: currentQuestions,
  };
  const [quiz, setQuiz] = useState(initialState);
  const currentUser = useSelector(getCurrentUser);
  let notQuestionElements = 0;

  const editableQuizStyle = css`
    width: 100%;
    .border-simple-text {
      border-radius: ${quarterSpacing}px;
      border-color: ${gray5};
    }
    margin: 0 ${standardSpacing}px;
    .bs4-form-control {
      background: ${white};
    }
    .tag {
      margin: ${halfSpacing}px 0;
      padding: 3px 12px 3px;
      border-radius: ${halfSpacing}px;
      height: ${standardSpacing}px;
      width: fit-content;
    }
    .title {
      padding: 0 ${standardSpacing}px;
    }
    .questions {
      padding-bottom: ${doubleSpacing}px;
    }
    .question {
      padding: 0 ${standardSpacing}px;
      background-color: ${gray7};
      .question-number {
        line-height: ${headerLineHeight}px;
        justify-self: center;
        padding: ${standardSpacing}px 0px ${halfSpacing}px;
      }
    }
    .separator {
      margin: ${largeSpacing}px 0 ${largeSpacing}px;
    }
    .options {
      padding-bottom: ${standardSpacing}px;
    }
  `;

  const save = useCallback(() => {
    const attributes = [];
    let name;
    if (quiz.activity.title !== initialState.activity.title) {
      name = 'title';
      attributes.push({
        translationId: currentQuiz.translationIds[name],
        name,
        text: quiz.activity.title,
        elementType: componentType,
        elementId: currentQuiz.id,
      });
    }
    quiz.questions.forEach((quizQuestion, quizQuestionIndex) => {
      Object.values(QuestionParameters).forEach(questionParameter => {
        if (quizQuestion.question[questionParameter] !== initialState.questions[quizQuestionIndex].question[questionParameter]) {
          name = questionParameter;
          if (quizQuestion.question.translationIds?.[name]) {
            attributes.push({
              translationId: quizQuestion.question.translationIds[name],
              name,
              text: quizQuestion.question[name],
              elementType: componentType,
              elementId: currentQuiz.id,
              questionId: quizQuestion.question.id,
            });
          }
        }
      });
      if (quizQuestion.options !== initialState.questions[quizQuestionIndex].options) {
        quizQuestion.options.forEach((option, index) => {
          Object.values(OptionParameters).forEach(optionParameter => {
            if (option[optionParameter] !== initialState.questions[quizQuestionIndex].options[index][optionParameter]) {
              name = optionParameter;
              if (option.translationIds?.[name]) {
                attributes.push({
                  translationId: option.translationIds[name],
                  name,
                  text: option[name],
                  elementType: componentType,
                  elementId: currentQuiz.id,
                  questionId: quizQuestion.question.id,
                  optionId: option.id,
                });
              }
            }
          });
        });
      }
      if (quizQuestion.hints && quizQuestion.hints !== initialState.questions[quizQuestionIndex].hints) {
        quizQuestion.hints.forEach((hint, index) => {
          if (hint.feedbackText !== initialState.questions[quizQuestionIndex].hints[index].feedbackText) {
            name = 'feedbackText';
            if (hint.translationIds?.[name]) {
              attributes.push({
                translationId: hint.translationIds[name],
                name,
                text: hint.feedbackText,
                elementType: componentType,
                elementId: currentQuiz.id,
                questionId: quizQuestion.question.id,
                feedbackId: hint.id,
              });
            }
          }
        });
      }
    });
    const payload = {
      id: lectureComponent.id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [quiz, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== quiz) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [quiz]);

  const setQuestionProperty = (property, questionIndex, newText) => {
    const newQuestions = [...quiz.questions];
    newQuestions[questionIndex] = {
      ...newQuestions[questionIndex],
      question: {
        ...newQuestions[questionIndex].question,
        [property]: newText,
      },
    };
    setQuiz({
      ...quiz,
      questions: newQuestions,
    });
  };

  const setOptionProperty = (property, questionIndex, optionIndex, newText) => {
    const newOptions = [...quiz.questions[questionIndex].options];
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      [property]: newText,
    };
    const updatedQuestions = [...quiz.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      options: newOptions,
    };
    setQuiz({
      ...quiz,
      questions: updatedQuestions,
    });
  };

  const setHint = (questionIndex, hintIndex, newText) => {
    const newQuestions = [...quiz.questions];
    newQuestions[questionIndex].hints[hintIndex] = {
      ...newQuestions[questionIndex].hints[hintIndex],
      feedbackText: newText,
    };
    setQuiz({
      ...quiz,
      questions: newQuestions,
    });
  };

  const setQuestionText = (questionIndex, newText) => {
    setQuestionProperty(QuestionParameters.QUESTION_TEXT, questionIndex, newText);
  };

  const setOptionText = (questionIndex, optionIndex, newText) => {
    setOptionProperty(OptionParameters.OPTION_CONTENT, questionIndex, optionIndex, newText);
  };

  const setFeedBackText = (questionIndex, optionIndex, newText, isSecondary = false) => {
    if (isSecondary) {
      setOptionProperty(OptionParameters.SECONDARY_EXPLANATION, questionIndex, optionIndex, newText);
    } else {
      setOptionProperty(OptionParameters.EXPLANATION, questionIndex, optionIndex, newText);
    }
  };

  const setAxisLabelText = (questionIndex, newText, isVertical = false) => {
    if (isVertical) {
      setQuestionProperty(QuestionParameters.VERTICAL_AXIS, questionIndex, newText);
    } else {
      setQuestionProperty(QuestionParameters.HORIZONTAL_AXIS, questionIndex, newText);
    }
  };

  return (
    <div className='editable-quiz' css={editableQuizStyle}>
      <div className='title'>
        <div className='tag bg-gray-6 text-small font-weight-bolder'>
          { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE() }
        </div>
        <QuizSimpleTextComponent
          value={quiz.activity.title}
          onChange={(newDescription) => { setQuiz({ ...quiz, activity: { title: newDescription } }); }}
        />
      </div>
      <div className='questions'>
        {
          quiz.questions?.map((currentQuestion, questionIndex) => {
            if (currentQuestion.question.type === QuizQuestionType.STATEMENT) {
              notQuestionElements += 1;
            }
            return (
              <React.Fragment key={`question-${currentQuestion.question.id}`}>
                <hr className='separator bg-gray-5' />
                <div className='question'>
                  <div className='question-number text-large-regular font-weight-bolder'>
                    {
                      currentQuestion.question.type !== QuizQuestionType.STATEMENT
                        ? t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.QUESTION_NUMBER(`${(questionIndex - notQuestionElements) + 1}`)
                        : t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.STATEMENT()
                    }
                  </div>
                  {
                    currentQuestion.question.type !== QuizQuestionType.STATEMENT && (
                      <div className='tag bg-gray-6 text-small font-weight-bolder'>
                        { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.QUESTION() }
                      </div>
                    )
                  }
                  <QuizRichTextElement
                    value={currentQuestion.question.questionText}
                    onChange={(newDescription) => { setQuestionText(questionIndex, newDescription); }}
                    containerClass={containerClass}
                    withCodeView
                  />
                  {
                    currentQuestion.question.horizontalAxisLabel
                    && (currentQuestion.question.type === QuizQuestionType.LIKERT_SCALE_QUESTION
                      || currentQuestion.question.type === QuizQuestionType.TABLE_NUMBER_QUESTION
                      || currentQuestion.question.type === QuizQuestionType.TABLE_SHORT_ANSWER_QUESTION
                    )
                    && (
                      <React.Fragment>
                        <div className='tag bg-gray-6 text-small font-weight-bolder'>
                          {
                            currentQuestion.question.type === QuizQuestionType.LIKERT_SCALE_QUESTION
                              ? t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.LEFT_LABEL()
                              : t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TABLE_HEADER()
                          }
                        </div>
                        <QuizSimpleTextComponent
                          value={currentQuestion.question.horizontalAxisLabel}
                          onChange={(newDescription) => { setAxisLabelText(questionIndex, newDescription); }}
                        />
                      </React.Fragment>
                    )
                  }
                  {
                    currentQuestion.question.verticalAxisLabel
                    && currentQuestion.question.type === QuizQuestionType.LIKERT_SCALE_QUESTION
                    && (
                      <React.Fragment>
                        <div className='tag bg-gray-6 text-small font-weight-bolder'>
                          { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.RIGHT_LABEL() }
                        </div>
                        <QuizSimpleTextComponent
                          value={currentQuestion.question.verticalAxisLabel}
                          onChange={(newDescription) => { setAxisLabelText(questionIndex, newDescription, true); }}
                        />
                      </React.Fragment>
                    )
                  }
                  <div className='options'>
                    {
                      currentQuestion.options?.map((option, optionIndex) => (
                        <React.Fragment key={`option-${option.id}`}>
                          <EditableOption
                            componentType={componentType}
                            question={currentQuestion.question}
                            questionIndex={questionIndex}
                            option={option}
                            optionIndex={optionIndex}
                            containerClass={containerClass}
                            setOptionText={setOptionText}
                            setFeedBackText={setFeedBackText}
                          />
                        </React.Fragment>
                      ))
                    }
                    {
                      currentQuestion.hints?.map((hint, hintIndex) => (
                        <React.Fragment key={`hint-${hint.id}`}>
                          <div className='tag bg-gray-6 text-small font-weight-bolder'>
                            { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.HINT_NUMBER(hintIndex + 1) }
                          </div>
                          <QuizRichTextElement
                            value={hint.feedbackText}
                            onChange={(newDescription) => { setHint(questionIndex, hintIndex, newDescription); }}
                            containerClass={containerClass}
                          />
                        </React.Fragment>
                      ))
                    }
                  </div>
                </div>
              </React.Fragment>
            );
          })
        }
      </div>
    </div>
  );
};

export default EditableQuizBase;
